import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card } from 'reactstrap';
import './discover-screen.css';

import useNavigation from '../../../../shared/utils/useNavigation';
import { useSwipe, usePaginationWindow } from '../../../../shared/hooks/useSwipe';
import { formatProfileTag, isUserLoggedIn } from '../../../../shared/utils/function';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import ModernFootermenu from '../../../../components/layout/Footer';
import DiscoverLoadingScreen from '../../../../components/loading/discover/DiscoverLoadingScreen';
import FeaturedCreatorCard from '../../../../features/cards/FeaturedCreatorCard';
import CreatorCardWithPadding from '../../../../features/cards/CreatorCardWithPadding';
import TrendingProfileCard from '../../../../features/cards/TrendingProfileCard';
import RecommendedProfileCard from '../../../../features/cards/RecommendedProfileCard';

import {
  fetchAllDiscoverData,
  fetchRecommendedProfilesAction,
  updateFollowStatus,
  setCategoryPage,
  setRecommendedPage,
  clearDiscoverData,
} from '../../../../shared/actions/discoverActions';
import { resolveVatRate } from '../../../../shared/utils/paymentUtil';
import { reportError } from '../../../../shared/sentry';

const PAGINATION_WINDOW_SIZE = 4;

const DiscoverScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const isGuestUser = !isUserLoggedIn();

  const {
    featuredProfiles,
    loadingFeatured,
    allCreators,
    loadingCreators,
    categories,
    loadingCategories,
    currentCategoryPage,
    allRecommendedProfiles,
    loadingRecommended,
    currentRecommendedPage,
    trendingProfiles,
    loadingTrending,
    isInitialized,
    fetchedForAuthState,
  } = useSelector((state) => state.discover);
  const userInfo = useSelector((state) => state.user.user);

  // Track previous authentication state to detect changes
  const prevAuthStateRef = useRef(isGuestUser);

  // UI State
  const [activeTab, setActiveTab] = useState('featured');
  const [isInitialLoading, setIsInitialLoading] = useState(!isInitialized);
  const [vatPercentage, setVatPercentage] = useState(0);

  // Constants
  const categoriesPerPage = 6;
  const profilesPerPage = 3;

  // Section refs for smooth scrolling
  const featuredRef = useRef(null);
  const allCreatorsRef = useRef(null);
  const categoriesRef = useRef(null);
  const recommendedRef = useRef(null);
  const trendingRef = useRef(null);
  const categoriesContainerRef = useRef(null);
  const recommendedContainerRef = useRef(null);

  // Event Handlers
  const scrollToSection = (sectionRef, tabName) => {
    setActiveTab(tabName);
    const sectionElement = sectionRef?.current;
    if (!sectionElement) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    sectionElement.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  const handleSeeAllCreators = () => {
    navigate.toAllCreators();
  };

  const handleSeeAllCategories = () => {
    navigate.toAllCategories();
  };

  const handleCategoryClick = (categoryName) => {
    navigate.toAllCreators(categoryName);
  };

  const handleFollowSuccess = (creatorId) => {
    dispatch(updateFollowStatus(creatorId, true));
  };

  const handleUnfollowSuccess = async (creatorId) => {
    dispatch(updateFollowStatus(creatorId, false));
  };

  const handleRefreshRecommended = () => {
    dispatch(fetchRecommendedProfilesAction(isGuestUser));
  };

  // Pagination Helpers
  const getCurrentPageRecommendedProfiles = () => {
    const startIndex = currentRecommendedPage * profilesPerPage;
    const endIndex = startIndex + profilesPerPage;
    return allRecommendedProfiles.slice(startIndex, endIndex);
  };

  const getTotalRecommendedPages = () => {
    return Math.ceil(allRecommendedProfiles.length / profilesPerPage);
  };

  const recommendedPaginationWindow = usePaginationWindow(
    getTotalRecommendedPages(),
    currentRecommendedPage,
    PAGINATION_WINDOW_SIZE
  );

  const handleRecommendedPageChange = (pageIndex) => {
    dispatch(setRecommendedPage(pageIndex));
  };

  const handleRecommendedSwipe = useCallback((startX, endX, startY = null, endY = null) => {
    const distanceX = startX - endX;
    const distanceY = startY != null && endY != null ? Math.abs(startY - endY) : 0;
    const minSwipeDistance = 50;
    const totalPages = Math.ceil(allRecommendedProfiles.length / profilesPerPage);

    if (startY != null && endY != null && distanceY > Math.abs(distanceX)) return;

    if (distanceX > minSwipeDistance && currentRecommendedPage < totalPages - 1) {
      handleRecommendedPageChange(currentRecommendedPage + 1);
    } else if (distanceX < -minSwipeDistance && currentRecommendedPage > 0) {
      handleRecommendedPageChange(currentRecommendedPage - 1);
    }
  }, [allRecommendedProfiles.length, currentRecommendedPage, profilesPerPage]);

  const recommendedSwipe = useSwipe({
    onSwipe: handleRecommendedSwipe,
    maxOffset: 100,
    ignoreSelector: '.pagination-dot, .refresh-link',
  });

  const getCurrentPageCategories = () => {
    const startIndex = currentCategoryPage * categoriesPerPage;
    const endIndex = startIndex + categoriesPerPage;
    return categories.slice(startIndex, endIndex);
  };

  const getTotalCategoryPages = () => {
    return Math.ceil(categories.length / categoriesPerPage);
  };

  const categoryPaginationWindow = usePaginationWindow(
    getTotalCategoryPages(),
    currentCategoryPage,
    PAGINATION_WINDOW_SIZE
  );

  const handleCategoryPageChange = (pageIndex) => {
    dispatch(setCategoryPage(pageIndex));
  };

  const handleCategorySwipe = useCallback((startX, endX, startY = null, endY = null) => {
    const distanceX = startX - endX;
    const distanceY = startY != null && endY != null ? Math.abs(startY - endY) : 0;
    const minSwipeDistance = 50;
    const totalPages = Math.ceil(categories.length / categoriesPerPage);

    if (startY != null && endY != null && distanceY > Math.abs(distanceX)) return;

    if (distanceX > minSwipeDistance && currentCategoryPage < totalPages - 1) {
      handleCategoryPageChange(currentCategoryPage + 1);
    } else if (distanceX < -minSwipeDistance && currentCategoryPage > 0) {
      handleCategoryPageChange(currentCategoryPage - 1);
    }
  }, [categories.length, categoriesPerPage, currentCategoryPage]);

  const categorySwipe = useSwipe({
    onSwipe: handleCategorySwipe,
    maxOffset: 100,
    ignoreSelector: '.pagination-dot',
  });

  // Effects - Scroll-based tab: active tab follows the section with the most visible area (smooth)
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -35% 0px', // top: header + tabs, bottom: favor section in upper viewport
      threshold: [0, 0.15, 0.3, 0.45, 0.6, 0.75, 0.9, 1],
    };

    const visibilityBySection = {};

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        const sectionName = entry.target.getAttribute('data-section');
        if (!sectionName) return;
        visibilityBySection[sectionName] = entry.isIntersecting ? entry.intersectionRatio : 0;
      });

      let bestSection = null;
      let bestRatio = 0;
      Object.entries(visibilityBySection).forEach(([name, ratio]) => {
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestSection = name;
        }
      });
      if (bestSection) {
        setActiveTab(bestSection);
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const sections = [
      { ref: featuredRef, name: 'featured' },
      { ref: allCreatorsRef, name: 'all' },
    ];
    if (trendingProfiles && trendingProfiles.length > 0) {
      sections.push({ ref: trendingRef, name: 'trending' });
    }
    sections.push({ ref: categoriesRef, name: 'categories' });
    if (!isGuestUser) {
      sections.push({ ref: recommendedRef, name: 'recommended' });
    }

    sections.forEach(({ ref, name }) => {
      if (ref.current) {
        ref.current.setAttribute('data-section', name);
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, [isGuestUser, trendingProfiles, isInitialLoading]);

  // Effect to handle initial data loading and auth state changes
  useEffect(() => {
    const loadData = async () => {
      setIsInitialLoading(true);
      try {
        await dispatch(fetchAllDiscoverData(isGuestUser));
        // Fetch Recommended profiles only when discover page is open
        if (!isGuestUser) {
          await dispatch(fetchRecommendedProfilesAction(isGuestUser));
        }
      } catch (error) {
        reportError(error, 'discover load data');
      } finally {
        setIsInitialLoading(false);
      }
    };

    const hasAuthStateChanged = prevAuthStateRef.current !== isGuestUser;
    const currentAuthState = isGuestUser ? 'guest' : 'authenticated';
    const isAuthStateMismatch = fetchedForAuthState && fetchedForAuthState !== currentAuthState;

    // Case 1: Initial load - no data in Redux yet
    if (!isInitialized) {
      loadData();
      prevAuthStateRef.current = isGuestUser;
    }
    // Case 2: Auth state changed (login/logout)
    else if (hasAuthStateChanged || isAuthStateMismatch) {
      // Clear existing data
      dispatch(clearDiscoverData());
      // Fetch fresh data based on new auth state
      loadData();
      // Update the ref to current auth state
      prevAuthStateRef.current = isGuestUser;
    }
    // Case 3: Data already exists and no auth change
    else {
      setIsInitialLoading(false);
      // Still fetch Recommended profiles when discover page is opened
      if (!isGuestUser) {
        dispatch(fetchRecommendedProfilesAction(isGuestUser));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuestUser, isInitialized, fetchedForAuthState]);

  // Effect to fetch VAT percentage
  useEffect(() => {
    const fetchVatPercentage = async () => {
      const vatPercentage = await resolveVatRate(userInfo);
      setVatPercentage(vatPercentage);
    };
    fetchVatPercentage();
  }, [userInfo]);

  // Show loading screen while initial data is loading
  if (isInitialLoading) {
    return <DiscoverLoadingScreen />;
  }

  return (
    <div className="discover-screen">
      <ModernHeader
        showButtonBorder={false}
      />

      {/* Navigation Tabs */}
      <div className="discover-nav-tabs">
        {(loadingFeatured || (featuredProfiles && featuredProfiles.length > 0)) && (
          <button
            className={`discover-nav-tab ${activeTab === 'featured' ? 'active' : ''}`}
            onClick={() => scrollToSection(featuredRef, 'featured')}
          >
            <span>Featured Creators</span>
            {activeTab === 'featured' && <div className="active-indicator"></div>}
          </button>
        )}
        {(loadingCreators || (allCreators && allCreators.length > 0)) && (
          <button
            className={`discover-nav-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => scrollToSection(allCreatorsRef, 'all')}
          >
            All Creators
            {activeTab === 'all' && <div className="active-indicator"></div>}
          </button>
        )}
        {trendingProfiles && trendingProfiles.length > 0 && (
          <button
            className={`discover-nav-tab ${activeTab === 'trending' ? 'active' : ''}`}
            onClick={() => scrollToSection(trendingRef, 'trending')}
          >
            Trending
            {activeTab === 'trending' && <div className="active-indicator"></div>}
          </button>
        )}
        {(loadingCategories || (categories && categories.length > 0)) && (
          <button
            className={`discover-nav-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => scrollToSection(categoriesRef, 'categories')}
          >
            Categories
            {activeTab === 'categories' && <div className="active-indicator"></div>}
          </button>
        )}
        {(loadingRecommended || (allRecommendedProfiles && allRecommendedProfiles.length > 0)) && (
          <button
            className={`discover-nav-tab ${activeTab === 'recommended' ? 'active' : ''}`}
            onClick={() => scrollToSection(recommendedRef, 'recommended')}
          >
            Recommended
            {activeTab === 'recommended' && <div className="active-indicator"></div>}
          </button>
        )}
      </div>

      {/* Main Content */}
      <main className="discover-main">
        <Container fluid className="px-0">
          {/* Featured Creators Section - Only show if there's data or loading */}
          {(loadingFeatured || featuredProfiles.length > 0) && (
            <section ref={featuredRef} className="featured-section mt-2">
              <div className="section-header">
                <h2 className="section-title">Featured Creators</h2>
              </div>
              <div className="featured-creators-container">
                {loadingFeatured ? (
                  <div className="loading-container">
                    <div className="loading-text">Loading featured creators...</div>
                  </div>
                ) : (
                  <div className="featured-row">
                    {featuredProfiles.map((creator) => (
                      <FeaturedCreatorCard
                        key={creator._id}
                        creator={creator}
                        vatPercentage={vatPercentage}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Trending Section */}
          {(loadingTrending || (trendingProfiles && trendingProfiles.length > 0)) && (
            <section ref={trendingRef} className="trending-section">
              <div className="section-header">
                <h2 className="section-title">Trending</h2>
              </div>
              <div className="trending-profiles-container">
                {loadingTrending ? (
                  <div className="loading-container">
                    <div className="loading-text">Loading trending profiles...</div>
                  </div>
                ) : (
                  <div className="trending-profiles-row">
                    {trendingProfiles.map((profile, index) => (
                      <TrendingProfileCard
                        key={profile._id}
                        index={index}
                        profile={profile}
                        vatPercentage={vatPercentage}
                        onFollowSuccess={handleFollowSuccess}
                        onUnfollowSuccess={handleUnfollowSuccess}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* All Creators Section - Only show if there's data or loading */}
          {(loadingCreators || allCreators.length > 0) && (
            <section ref={allCreatorsRef} className="all-creators-section">
              <div className="section-header">
                <h2 className="section-title">All Creators</h2>
                <span
                  className="see-all-link cursor-pointer"
                  onClick={handleSeeAllCreators}
                >
                  See all
                </span>
              </div>
              <div className="creators-grid">
                {loadingCreators ? (
                  <div className="loading-container">
                    <div className="loading-text">Loading creators...</div>
                  </div>
                ) : (
                  <div className="creators-row">
                    {allCreators.map((creator) => (
                      <CreatorCardWithPadding
                        key={creator._id}
                        creator={creator}
                        vatPercentage={vatPercentage}
                        tab="all-creators"
                        onFollowSuccess={handleFollowSuccess}
                        onUnfollowSuccess={handleUnfollowSuccess}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Categories Section - Only show if there's data or loading */}
          {(loadingCategories || categories.length > 0) && (
            <section ref={categoriesRef} className="categories-section">
              <div className="section-header">
                <h2 className="section-title">Categories</h2>
                <span
                  className="see-all-link cursor-pointer"
                  onClick={handleSeeAllCategories}
                >
                  See all
                </span>
              </div>
              <div
                ref={(el) => {
                  categoriesContainerRef.current = el;
                  if (categorySwipe.ref) categorySwipe.ref(el);
                }}
                className={`categories-grid categories-swipeable ${categorySwipe.swipeOffset !== 0 ? 'swiping' : ''}`}
                {...categorySwipe.handlers}
              >
                {loadingCategories ? (
                  <div className="loading-categories-container">
                    <div className="loading-text">Loading categories...</div>
                  </div>
                ) : (
                  <div
                    className="categories-wrapper"
                    style={{
                      transform: `translateX(${categorySwipe.swipeOffset}px)`,
                      opacity: categorySwipe.swipeOffset !== 0
                        ? Math.max(0.85, 1 - Math.abs(categorySwipe.swipeOffset) / 200)
                        : 1,
                    }}
                  >
                    <Row className="categories-row">
                      {getCurrentPageCategories().map((category, index) => (
                        <Col key={`${currentCategoryPage}-${index}`} xs="5" className="category-col cm-category-size">
                          <Card
                            className="category-card cursor-pointer"
                            onClick={() => handleCategoryClick(category.name)}
                          >
                            <div className="category-content">
                              <span className="category-name">
                                {formatProfileTag(category.name)}
                              </span>
                              <p className="category-count">
                                {category.creators} {category.creators > 1 ? 'creators' : 'creator'}
                              </p>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
              </div>
              {!loadingCategories && categories.length > categoriesPerPage && (
                <div className="category-pagination">
                  <div className="pagination-dots">
                    {categoryPaginationWindow.map((index) => (
                      <span
                        key={index}
                        className={`pagination-dot cursor-pointer ${currentCategoryPage === index ? 'active' : ''}`}
                        onClick={() => handleCategoryPageChange(index)}
                      ></span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Recommended Section - Only show if there's data or loading AND not guest user */}
          {!isGuestUser && (loadingRecommended || allRecommendedProfiles.length > 0) && (
            <section ref={recommendedRef} className="recommended-section">
              <div className="section-header">
                <h2 className="section-title">Recommended</h2>
                <span
                  className="refresh-link cursor-pointer"
                  onClick={handleRefreshRecommended}
                >
                  Refresh
                </span>
              </div>
              <div
                ref={(el) => {
                  recommendedContainerRef.current = el;
                  if (recommendedSwipe.ref) recommendedSwipe.ref(el);
                }}
                className={`recommended_profiles_min_height recommended-swipeable ${recommendedSwipe.swipeOffset !== 0 ? 'swiping' : ''}`}
                {...recommendedSwipe.handlers}
              >
                <div
                  className="recommended-profiles-wrapper"
                  style={{
                    transform: `translateX(${recommendedSwipe.swipeOffset}px)`,
                    opacity: recommendedSwipe.swipeOffset !== 0
                      ? Math.max(0.85, 1 - Math.abs(recommendedSwipe.swipeOffset) / 200)
                      : 1,
                  }}
                >
                  <div className="recommended-profiles">
                    {loadingRecommended ? (
                      <div className="loading-container">
                        <div className="loading-text">Loading recommended profiles...</div>
                      </div>
                    ) : (
                      getCurrentPageRecommendedProfiles().map((profile) => (
                        <RecommendedProfileCard
                          key={`${currentRecommendedPage}-${profile._id}`}
                          profile={profile}
                          vatPercentage={vatPercentage}
                          onFollowSuccess={handleFollowSuccess}
                          onUnfollowSuccess={handleUnfollowSuccess}
                        />
                      ))
                    )}
                  </div>
                </div>
                {!loadingRecommended && getTotalRecommendedPages() > 0 && (
                  <div className="recommended-pagination">
                    <div className="pagination-dots">
                      {recommendedPaginationWindow.map((index) => (
                        <span
                          key={index}
                          className={`pagination-dot cursor-pointer ${currentRecommendedPage === index ? 'active' : ''}`}
                          onClick={() => handleRecommendedPageChange(index)}
                        ></span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
        </Container>
      </main>

      <ModernFootermenu />
    </div>
  );
};

export default DiscoverScreen;